import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { FILTER_FIELDS, operatorsForFieldType } from '../constants/taskFields';
import { dueDateColumnLabel } from '../services/taskTimezoneUtils';

function fieldLabel(fieldKey, timezone) {
  const meta = FILTER_FIELDS.find((f) => f.key === fieldKey);
  if (!meta) return fieldKey;
  if (meta.type === 'date') {
    return `${meta.label} (${dueDateColumnLabel(timezone).match(/\(([^)]+)\)/)?.[1] || ''})`.replace('Due Date', meta.label);
  }
  return meta.label;
}

function cloneCondition(condition) {
  const value = condition?.value;
  return {
    field: condition?.field || '',
    operator: condition?.operator || '',
    value: value && typeof value === 'object' && !Array.isArray(value) ? { ...value } : (value ?? ''),
  };
}

function emptyCondition() {
  return { field: '', operator: '', value: '' };
}

/** Validity rule shared by Apply and the Filters badge count. */
export function isCompleteCondition(condition) {
  return Boolean(condition?.field && condition?.operator);
}

/**
 * Count valid conditions across an applied advanced-filter payload,
 * whether it is a flat AND list or nested OR-of-AND groups. The Filters
 * badge uses this so its count always matches what the query applies.
 */
export function countActiveAdvancedFilters(filters) {
  if (!Array.isArray(filters)) return 0;
  return filters.reduce((total, item) => {
    if (!item || typeof item !== 'object') return total;
    if (!item.field && Array.isArray(item.filters)) {
      return total + countActiveAdvancedFilters(item.filters);
    }
    return total + (isCompleteCondition(item) ? 1 : 0);
  }, 0);
}

/**
 * Convert the applied filter payload back into editable groups.
 * Payload shapes:
 * - []                                        -> no groups
 * - [cond, cond]                              -> one AND group (legacy flat shape)
 * - [{ logic:'or', filters:[{ logic:'and', filters:[...] }, ...] }] -> OR of AND groups
 */
export function groupsFromFilters(filters) {
  if (!Array.isArray(filters) || filters.length === 0) return [];
  const only = filters.length === 1 ? filters[0] : null;
  if (only && !only.field && Array.isArray(only.filters)) {
    return only.filters.map((entry) => ({
      conditions: (Array.isArray(entry?.filters) ? entry.filters : [entry]).map(cloneCondition),
    }));
  }
  return [{ conditions: filters.map(cloneCondition) }];
}

/**
 * Convert editable groups into the applied filter payload, dropping
 * incomplete conditions and empty groups. A single group stays flat
 * (AND list) for backward compatibility; multiple groups become an
 * OR of AND sub-groups, which the backend compiles recursively.
 */
export function filtersFromGroups(groups, normalizeCondition = (c) => c) {
  const compiled = (groups || [])
    .map((group) => (group.conditions || [])
      .filter(isCompleteCondition)
      .map(normalizeCondition))
    .filter((conditions) => conditions.length > 0);
  if (compiled.length === 0) return [];
  if (compiled.length === 1) return compiled[0];
  return [{
    logic: 'or',
    filters: compiled.map((conditions) => ({ logic: 'and', filters: conditions })),
  }];
}

function LogicSeparator({ label }) {
  return (
    <div
      role="separator"
      aria-label={label}
      data-testid={`task-filter-${label.toLowerCase()}-separator`}
      className="flex items-center gap-2 py-0.5"
    >
      <span aria-hidden="true" className="h-px flex-1 bg-border" />
      <span className="rounded-full border border-border bg-muted px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span aria-hidden="true" className="h-px flex-1 bg-border" />
    </div>
  );
}

export function TaskAdvancedFilterDrawer({
  open,
  onOpenChange,
  filters,
  timezone,
  onApply,
}) {
  const [groups, setGroups] = useState(() => groupsFromFilters(filters));
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) setGroups(groupsFromFilters(filters));
  }, [open, filters]);

  const filteredFields = FILTER_FIELDS.filter((f) => (
    !search || f.label.toLowerCase().includes(search.toLowerCase())
  ));

  const addFirstGroup = (fieldKey) => {
    const meta = FILTER_FIELDS.find((f) => f.key === fieldKey);
    const ops = operatorsForFieldType(meta?.type);
    setGroups((prev) => [
      ...prev,
      { conditions: [{ field: fieldKey, operator: ops[0]?.value || 'is', value: '' }] },
    ]);
  };

  const addGroup = () => {
    setGroups((prev) => [...prev, { conditions: [emptyCondition()] }]);
  };

  const addNestedCondition = (groupIndex) => {
    setGroups((prev) => prev.map((group, g) => (
      g === groupIndex
        ? { ...group, conditions: [...group.conditions, emptyCondition()] }
        : group
    )));
  };

  const updateCondition = (groupIndex, conditionIndex, patch) => {
    setGroups((prev) => prev.map((group, g) => (
      g === groupIndex
        ? {
            ...group,
            conditions: group.conditions.map((condition, c) => (
              c === conditionIndex ? { ...condition, ...patch } : condition
            )),
          }
        : group
    )));
  };

  const removeCondition = (groupIndex, conditionIndex) => {
    setGroups((prev) => prev.map((group, g) => (
      g === groupIndex
        ? { ...group, conditions: group.conditions.filter((_, c) => c !== conditionIndex) }
        : group
    )));
  };

  const removeGroup = (groupIndex) => {
    setGroups((prev) => prev.filter((_, g) => g !== groupIndex));
  };

  const needsValue = (operator) => ![
    'is_empty',
    'is_not_empty',
    'is_assigned',
    'is_unassigned',
    'today',
    'tomorrow',
    'yesterday',
    'this_week',
    'this_month',
    'this_quarter',
    'this_year',
    'overdue',
  ].includes(operator);

  const normalizeCondition = (f) => {
    if (f.operator === 'between' && f.value && typeof f.value === 'object') {
      return {
        field: f.field,
        operator: f.operator,
        value: {
          start: f.value.start ? new Date(`${f.value.start}T00:00:00Z`).toISOString() : null,
          end: f.value.end ? new Date(`${f.value.end}T23:59:59Z`).toISOString() : null,
        },
      };
    }
    if (typeof f.value === 'string' && f.value && FILTER_FIELDS.find((x) => x.key === f.field)?.type === 'date') {
      return { field: f.field, operator: f.operator, value: new Date(`${f.value}T00:00:00Z`).toISOString() };
    }
    return { field: f.field, operator: f.operator, value: f.value };
  };

  const renderConditionRow = (condition, groupIndex, conditionIndex) => {
    const meta = FILTER_FIELDS.find((f) => f.key === condition.field);
    const ops = operatorsForFieldType(meta?.type);
    const incomplete = !condition.field || !condition.operator;
    return (
      <div data-testid="task-filter-condition" className="space-y-2">
        <div className="flex gap-2">
          <Select
            value={condition.field || undefined}
            onValueChange={(field) => {
              const nextMeta = FILTER_FIELDS.find((f) => f.key === field);
              const nextOps = operatorsForFieldType(nextMeta?.type);
              updateCondition(groupIndex, conditionIndex, {
                field,
                operator: nextOps[0]?.value || 'is',
                value: '',
              });
            }}
          >
            <SelectTrigger className="flex-1" aria-label="Filter field">
              <SelectValue placeholder="Please select" />
            </SelectTrigger>
            <SelectContent>
              {FILTER_FIELDS.map((f) => (
                <SelectItem key={f.key} value={f.key}>
                  {fieldLabel(f.key, timezone)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            size="icon"
            variant="outline"
            aria-label="Remove condition"
            data-testid="task-filter-remove-condition"
            onClick={() => removeCondition(groupIndex, conditionIndex)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        <Select
          value={condition.operator || undefined}
          onValueChange={(operator) => updateCondition(groupIndex, conditionIndex, { operator })}
          disabled={!condition.field}
        >
          <SelectTrigger aria-label="Filter operator">
            <SelectValue placeholder="Please select" />
          </SelectTrigger>
          <SelectContent>
            {ops.map((op) => (
              <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {incomplete ? (
          <Input disabled placeholder="Please input" />
        ) : needsValue(condition.operator) ? (
          meta?.type === 'date' && (condition.operator === 'between') ? (
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="date"
                value={condition.value?.start || ''}
                onChange={(e) => updateCondition(groupIndex, conditionIndex, {
                  value: { ...(condition.value || {}), start: e.target.value },
                })}
              />
              <Input
                type="date"
                value={condition.value?.end || ''}
                onChange={(e) => updateCondition(groupIndex, conditionIndex, {
                  value: { ...(condition.value || {}), end: e.target.value },
                })}
              />
            </div>
          ) : meta?.type === 'date' ? (
            <Input
              type="date"
              value={typeof condition.value === 'string' ? condition.value : ''}
              onChange={(e) => updateCondition(groupIndex, conditionIndex, { value: e.target.value })}
            />
          ) : meta?.type === 'status' && condition.field === 'status' ? (
            <Select
              value={condition.value || 'pending'}
              onValueChange={(value) => updateCondition(groupIndex, conditionIndex, { value })}
            >
              <SelectTrigger><SelectValue placeholder="Please select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Input
              placeholder="Value"
              value={typeof condition.value === 'string' ? condition.value : ''}
              onChange={(e) => updateCondition(groupIndex, conditionIndex, { value: e.target.value })}
            />
          )
        ) : null}
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col p-0 sm:max-w-lg" data-testid="task-advanced-filters">
        <SheetHeader className="border-b border-border px-6 py-4 text-left">
          <SheetTitle>Filters</SheetTitle>
        </SheetHeader>

        <div className="space-y-4 overflow-y-auto px-6 py-4">
          <Input
            placeholder="Search fields"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {groups.length === 0 ? (
            <div className="space-y-2">
              {['Task Info', 'Associations', 'Other details'].map((category) => (
                <div key={category}>
                  <p className="mb-1 bg-muted/60 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {category}
                  </p>
                  {filteredFields
                    .filter((f) => f.category === category)
                    .map((field) => (
                      <button
                        key={field.key}
                        type="button"
                        className="flex w-full items-center justify-between border-b border-border px-2 py-2.5 text-left text-sm hover:bg-muted/40"
                        onClick={() => addFirstGroup(field.key)}
                      >
                        {fieldLabel(field.key, timezone)}
                        <Plus className="h-4 w-4 text-muted-foreground" />
                      </button>
                    ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              <div data-testid="task-filter-groups" className="space-y-3">
                {groups.map((group, groupIndex) => (
                  <React.Fragment key={groupIndex}>
                    {groupIndex > 0 ? <LogicSeparator label="OR" /> : null}
                    <div data-testid="task-filter-group" className="rounded-md border border-border p-3 space-y-2">
                      <div data-testid="task-filter-conditions" className="space-y-2">
                        {group.conditions.map((condition, conditionIndex) => (
                          <React.Fragment key={conditionIndex}>
                            {conditionIndex > 0 ? <LogicSeparator label="AND" /> : null}
                            {renderConditionRow(condition, groupIndex, conditionIndex)}
                          </React.Fragment>
                        ))}
                      </div>
                      <button
                        type="button"
                        aria-label="Add nested filter"
                        data-testid="task-filter-add-nested"
                        className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-teal-500"
                        onClick={() => addNestedCondition(groupIndex)}
                      >
                        <Plus className="h-4 w-4" />
                        Add nested filter
                      </button>
                      {groups.length > 1 ? (
                        <div className="flex justify-end">
                          <button
                            type="button"
                            aria-label="Remove filter group"
                            data-testid="task-filter-remove-group"
                            className="rounded-md px-2 py-1 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-destructive"
                            onClick={() => removeGroup(groupIndex)}
                          >
                            Remove Filter
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </React.Fragment>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                aria-label="Add filter"
                data-testid="task-filter-add-group"
                onClick={addGroup}
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Add Filter
              </Button>
            </div>
          )}
        </div>

        <SheetFooter className="mt-auto flex flex-row items-center justify-between gap-2 border-t border-border px-6 py-4">
          <Button type="button" variant="ghost" onClick={() => setGroups([])}>
            Clear all filters
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              type="button"
              onClick={() => onApply?.(filtersFromGroups(groups, normalizeCondition))}
            >
              Apply
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default TaskAdvancedFilterDrawer;
